'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';


type Mode = 'login' | 'signup';

const roles = [
  { value: 'lekarz_poz', label: 'Lekarz POZ' },
  { value: 'lekarz_spec', label: 'Lekarz Specjalista' },
  { value: 'koordynator', label: 'Koordynator Leczenia' },
  { value: 'administrator', label: 'Administrator' },
];

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  role: string;
  pwz: string;
  terms: boolean;
}

export default function AuthForm() {
  const [mode, setMode] = useState<Mode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const loginForm = useForm<LoginFormData>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupFormData>({ defaultValues: { email: '', password: '', confirmPassword: '', firstName: '', lastName: '', role: '', pwz: '', terms: false } });

  const handleLogin = async (data: LoginFormData) => {
    setLoading(true);
    setAuthError('');
    // TODO: POST /api/auth/login
    await new Promise(r => setTimeout(r, 900));
    toast.success('Zalogowano pomyślnie. Przekierowanie...');
    setTimeout(() => { window.location.href = '/patient-dashboard'; }, 800);
    setLoading(false);
  };

  const handleSignup = async (data: SignupFormData) => {
    setLoading(true);
    // TODO: POST /api/auth/register
    await new Promise(r => setTimeout(r, 1000));
    toast.success('Konto utworzone. Sprawdź e-mail w celu weryfikacji.');
    setMode('login');
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-[48%] flex-col justify-between bg-[hsl(174,82%,24%)] p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 rounded-full border-2 border-white" />
          <div className="absolute top-40 left-32 w-40 h-40 rounded-full border border-white" />
          <div className="absolute bottom-32 right-8 w-80 h-80 rounded-full border-2 border-white" />
          <div className="absolute bottom-10 right-20 w-48 h-48 rounded-full border border-white" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={40} />
            <span className="text-white font-semibold text-xl tracking-tight">e-DiLO</span>
          </div>
          <h2 className="text-white text-3xl font-bold leading-tight mb-4">
            WERSJA DEMO z DANYMI TESTOWYMI<br />System Zarządzania<br />Kartami Onkologicznymi
          </h2>
          <p className="text-white/70 text-base leading-relaxed max-w-sm">
            Cyfrowe zarządzanie Kartami Diagnostyki i Leczenia Onkologicznego (DiLO) zgodnie z rozporządzeniem Ministra Zdrowia z dnia 9 kwietnia 2025 r. (Dz. U. poz. 490).
          </p>
        </div>
        <div className="relative space-y-4">
          {[
            { label: 'Pełna zgodność z rozporządzeniem MZ 2025', icon: '✓' },
            { label: 'Obsługa wszystkich sekcji A–H formularza DiLO', icon: '✓' },
            { label: 'Integracja z systemami NFZ', icon: '✓' },
            { label: 'Bezpieczne przechowywanie danych pacjentów', icon: '✓' },
          ].map((item, i) => (
            <div key={`feature-${i}`} className="flex items-center gap-3">
              <span className="w-5 h-5 rounded-full bg-white/20 text-white text-[11px] flex items-center justify-center font-bold shrink-0">{item.icon}</span>
              <span className="text-white/80 text-[14px]">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[hsl(210,20%,98%)]">
        <div className="w-full max-w-md">
          {/* Karta Pacjenta Logo */}
          <div className="flex flex-col items-center mb-8">
            <img
              src="/assets/images/result-1774785485348.jpeg"
              alt="Karta Pacjenta logo"
              className="h-20 w-auto object-contain"
            />
          </div>

          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={36} />
            <span className="font-semibold text-lg text-[hsl(220,15%,15%)]">e-DiLO</span>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-[hsl(220,15%,15%)]">
              {mode === 'login' ? 'Zaloguj się' : 'Utwórz konto'}
            </h1>
            <p className="text-sm text-[hsl(215,12%,50%)] mt-1">
              {mode === 'login' ? 'Wprowadź dane logowania, aby uzyskać dostęp' : 'Wypełnij formularz, aby zarejestrować nowe konto'}
            </p>
          </div>

          {/* Tab switcher */}
          <div className="flex bg-[hsl(210,15%,94%)] rounded-lg p-1 mb-6">
            {(['login', 'signup'] as Mode[]).map((m) => (
              <button
                key={`tab-${m}`}
                onClick={() => { setMode(m); setAuthError(''); }}
                className={`flex-1 text-[13px] font-medium py-2 rounded-md transition-all duration-200 ${
                  mode === m
                    ? 'bg-white text-[hsl(220,15%,15%)] shadow-sm'
                    : 'text-[hsl(215,12%,50%)] hover:text-[hsl(220,15%,15%)] hover:bg-white/50'
                }`}
              >
                {m === 'login' ? 'Logowanie' : 'Rejestracja'}
              </button>
            ))}
          </div>

          {/* Auth error */}
          {authError && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5">
              <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
              <p className="text-[13px] text-red-600 font-medium">{authError}</p>
            </div>
          )}

          {/* Shared input class */}
          {mode === 'login' ? (
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-5">
              <div>
                <label className="block text-[13px] font-medium text-[hsl(220,15%,15%)] mb-1.5">Adres e-mail</label>
                <input
                  type="email"
                  {...loginForm.register('email', { required: 'Adres e-mail jest wymagany' })}
                  placeholder="lekarz@szpital.pl"
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 placeholder:text-[hsl(215,12%,65%)] ${
                    loginForm.formState.errors.email
                      ? 'border-red-400 bg-red-50/60 focus:ring-red-400/70' :'border-[hsl(214,15%,88%)] bg-white hover:border-[hsl(174,82%,35%)] focus:ring-[hsl(174,82%,24%)]/60 focus:border-[hsl(174,82%,24%)] focus:border-transparent'
                  }`}
                />
                {loginForm.formState.errors.email && (
                  <p className="flex items-center gap-1.5 text-red-600 text-[12px] mt-1.5 font-medium">
                    <AlertCircle size={13} className="shrink-0 text-red-500" />
                    <span>{loginForm.formState.errors.email.message}</span>
                  </p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[13px] font-medium text-[hsl(220,15%,15%)]">Hasło</label>
                  <button type="button" className="text-[12px] text-[hsl(174,82%,24%)] hover:text-[hsl(174,82%,18%)] hover:underline transition-colors duration-150">Zapomniałeś hasła?</button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...loginForm.register('password', { required: 'Hasło jest wymagane' })}
                    placeholder="••••••••"
                    className={`w-full border rounded-lg px-3 py-2.5 pr-10 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 placeholder:text-[hsl(215,12%,65%)] ${
                      loginForm.formState.errors.password
                        ? 'border-red-400 bg-red-50/60 focus:ring-red-400/70' :'border-[hsl(214,15%,88%)] bg-white hover:border-[hsl(174,82%,35%)] focus:ring-[hsl(174,82%,24%)]/60 focus:border-[hsl(174,82%,24%)] focus:border-transparent'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(215,12%,50%)] hover:text-[hsl(220,15%,15%)] transition-colors duration-150"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="flex items-center gap-1.5 text-red-600 text-[12px] mt-1.5 font-medium">
                    <AlertCircle size={13} className="shrink-0 text-red-500" />
                    <span>{loginForm.formState.errors.password.message}</span>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="remember" {...loginForm.register('remember')} className="accent-[hsl(174,82%,24%)] w-4 h-4 cursor-pointer" />
                <label htmlFor="remember" className="text-[13px] text-[hsl(215,12%,40%)] cursor-pointer select-none">Zapamiętaj mnie</label>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-[hsl(174,82%,24%)] hover:bg-[hsl(174,82%,20%)] hover:shadow-md active:scale-[0.98] text-white font-medium py-2.5 rounded-lg text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none mt-1"
              >
                {loading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Logowanie...</span></> : 'Zaloguj się'}
              </button>
            </form>
          ) : (
            <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[13px] font-medium text-[hsl(220,15%,15%)] mb-1.5">Imię <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    {...signupForm.register('firstName', { required: 'Wymagane' })}
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                      signupForm.formState.errors.firstName
                        ? 'border-red-400 bg-red-50/60 focus:ring-red-400/70' :'border-[hsl(214,15%,88%)] bg-white hover:border-[hsl(174,82%,35%)] focus:ring-[hsl(174,82%,24%)]/60 focus:border-[hsl(174,82%,24%)] focus:border-transparent'
                    }`}
                  />
                  {signupForm.formState.errors.firstName && (
                    <p className="flex items-center gap-1.5 text-red-600 text-[12px] mt-1.5 font-medium">
                      <AlertCircle size={13} className="shrink-0 text-red-500" />
                      <span>{signupForm.formState.errors.firstName.message}</span>
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[hsl(220,15%,15%)] mb-1.5">Nazwisko <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    {...signupForm.register('lastName', { required: 'Wymagane' })}
                    className={`w-full border rounded-lg px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                      signupForm.formState.errors.lastName
                        ? 'border-red-400 bg-red-50/60 focus:ring-red-400/70' :'border-[hsl(214,15%,88%)] bg-white hover:border-[hsl(174,82%,35%)] focus:ring-[hsl(174,82%,24%)]/60 focus:border-[hsl(174,82%,24%)] focus:border-transparent'
                    }`}
                  />
                  {signupForm.formState.errors.lastName && (
                    <p className="flex items-center gap-1.5 text-red-600 text-[12px] mt-1.5 font-medium">
                      <AlertCircle size={13} className="shrink-0 text-red-500" />
                      <span>{signupForm.formState.errors.lastName.message}</span>
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[hsl(220,15%,15%)] mb-1.5">Rola <span className="text-red-500">*</span></label>
                <select
                  {...signupForm.register('role', { required: 'Wybierz rolę' })}
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 cursor-pointer ${
                    signupForm.formState.errors.role
                      ? 'border-red-400 bg-red-50/60 focus:ring-red-400/70' :'border-[hsl(214,15%,88%)] bg-white hover:border-[hsl(174,82%,35%)] focus:ring-[hsl(174,82%,24%)]/60 focus:border-[hsl(174,82%,24%)] focus:border-transparent'
                  }`}
                >
                  <option value="">— wybierz rolę —</option>
                  {roles.map(r => <option key={`role-${r.value}`} value={r.value}>{r.label}</option>)}
                </select>
                {signupForm.formState.errors.role && (
                  <p className="flex items-center gap-1.5 text-red-600 text-[12px] mt-1.5 font-medium">
                    <AlertCircle size={13} className="shrink-0 text-red-500" />
                    <span>{signupForm.formState.errors.role.message}</span>
                  </p>
                )}
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[hsl(220,15%,15%)] mb-1.5">Numer PWZ (Prawo Wykonywania Zawodu)</label>
                <p className="text-[12px] text-[hsl(215,12%,50%)] mb-1.5">Wymagany dla lekarzy i personelu medycznego</p>
                <input
                  type="text"
                  {...signupForm.register('pwz')}
                  placeholder="np. 1234567"
                  className="w-full border border-[hsl(214,15%,88%)] rounded-lg px-3 py-2.5 text-sm font-mono transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 bg-white hover:border-[hsl(174,82%,35%)] focus:ring-[hsl(174,82%,24%)]/60 focus:border-[hsl(174,82%,24%)] focus:border-transparent placeholder:text-[hsl(215,12%,65%)]"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[hsl(220,15%,15%)] mb-1.5">Adres e-mail <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  {...signupForm.register('email', { required: 'Wymagane' })}
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                    signupForm.formState.errors.email
                      ? 'border-red-400 bg-red-50/60 focus:ring-red-400/70' :'border-[hsl(214,15%,88%)] bg-white hover:border-[hsl(174,82%,35%)] focus:ring-[hsl(174,82%,24%)]/60 focus:border-[hsl(174,82%,24%)] focus:border-transparent'
                  }`}
                />
                {signupForm.formState.errors.email && (
                  <p className="flex items-center gap-1.5 text-red-600 text-[12px] mt-1.5 font-medium">
                    <AlertCircle size={13} className="shrink-0 text-red-500" />
                    <span>{signupForm.formState.errors.email.message}</span>
                  </p>
                )}
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[hsl(220,15%,15%)] mb-1.5">Hasło <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...signupForm.register('password', { required: 'Wymagane', minLength: { value: 8, message: 'Minimum 8 znaków' } })}
                    className={`w-full border rounded-lg px-3 py-2.5 pr-10 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                      signupForm.formState.errors.password
                        ? 'border-red-400 bg-red-50/60 focus:ring-red-400/70' :'border-[hsl(214,15%,88%)] bg-white hover:border-[hsl(174,82%,35%)] focus:ring-[hsl(174,82%,24%)]/60 focus:border-[hsl(174,82%,24%)] focus:border-transparent'
                    }`}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(215,12%,50%)] hover:text-[hsl(220,15%,15%)] transition-colors duration-150">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="flex items-center gap-1.5 text-red-600 text-[12px] mt-1.5 font-medium">
                    <AlertCircle size={13} className="shrink-0 text-red-500" />
                    <span>{signupForm.formState.errors.password.message}</span>
                  </p>
                )}
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[hsl(220,15%,15%)] mb-1.5">Potwierdź hasło <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    {...signupForm.register('confirmPassword', { required: 'Wymagane' })}
                    className={`w-full border rounded-lg px-3 py-2.5 pr-10 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                      signupForm.formState.errors.confirmPassword
                        ? 'border-red-400 bg-red-50/60 focus:ring-red-400/70' :'border-[hsl(214,15%,88%)] bg-white hover:border-[hsl(174,82%,35%)] focus:ring-[hsl(174,82%,24%)]/60 focus:border-[hsl(174,82%,24%)] focus:border-transparent'
                    }`}
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(215,12%,50%)] hover:text-[hsl(220,15%,15%)] transition-colors duration-150">
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="flex items-center gap-1.5 text-red-600 text-[12px] mt-1.5 font-medium">
                    <AlertCircle size={13} className="shrink-0 text-red-500" />
                    <span>{signupForm.formState.errors.confirmPassword.message}</span>
                  </p>
                )}
              </div>
              <div className="flex items-start gap-2.5 pt-1">
                <input type="checkbox" id="terms" {...signupForm.register('terms', { required: 'Akceptacja regulaminu jest wymagana' })} className="accent-[hsl(174,82%,24%)] w-4 h-4 mt-0.5 cursor-pointer shrink-0" />
                <label htmlFor="terms" className="text-[13px] text-[hsl(215,12%,40%)] cursor-pointer select-none leading-relaxed">
                  Akceptuję <span className="text-[hsl(174,82%,24%)] hover:underline cursor-pointer">regulamin</span> i <span className="text-[hsl(174,82%,24%)] hover:underline cursor-pointer">politykę prywatności</span>
                </label>
              </div>
              {signupForm.formState.errors.terms && (
                <p className="flex items-center gap-1.5 text-red-600 text-[12px] font-medium -mt-2">
                  <AlertCircle size={13} className="shrink-0 text-red-500" />
                  <span>{signupForm.formState.errors.terms.message}</span>
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-[hsl(174,82%,24%)] hover:bg-[hsl(174,82%,20%)] hover:shadow-md active:scale-[0.98] text-white font-medium py-2.5 rounded-lg text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none mt-1"
              >
                {loading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Tworzenie konta...</span></> : 'Utwórz konto'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}